// Centralized pricing and credits configuration
// This is the SINGLE SOURCE OF TRUTH for all pricing-related values
// Updated Feb 2026: Credits halved for margin optimization, text/image costs aligned

// ===== ADD-ON CREDIT EXPIRY =====
// IMPORTANT: Add-on credits expire 90 days from LAST purchase
// This is different from subscription credits which expire after 30 days
// 
// CONSUMPTION ORDER:
//   1. Subscription credits (30-day expiry) - Used FIRST
//   2. Add-on credits (90-day expiry) - Used SECOND

export const PRICING_CONFIG = {
  // Free tier (ONE-TIME credits, never reset)
  FREE_CREDITS: 40,
  FREE_POSTS_EQUIVALENT: 3,
  
  // Add-on expiry (days)
  ADDON_EXPIRY_DAYS: 90,
  
  // Credit costs per action (for internal use - NOT shown to users)
  // Updated Feb 2026: Aligned to actual API costs
  //   - Text (GPT-5.2): ₹2-4 → 4 credits
  //   - Image (Nano Banana): ₹8-15 → 8 credits
  CREDIT_COSTS: {
    TEXT_GENERATION: 4,   // Was 8, now 4 (cheaper API)
    IMAGE_GENERATION: 8,  // Was 4, now 8 (expensive API)
    FULL_POST: 12,        // 4 + 8 = 12 per idea
    TEXT_REFINEMENT: 2,
    IMAGE_REFINEMENT: 4,  // Was 2, now 4 (matches image cost)
  },
  
  // Subscription plans (PRIMARY product - credits expire monthly but auto-renew)
  // NO BONUS CREDITS - simple, straightforward pricing
  // Updated Feb 2026: Credits halved for 90-96% margins
  SUBSCRIPTIONS: [
    {
      id: 'starter_monthly',
      name: 'Starter',
      price: 5499,
      priceDisplay: '₹5,499/mo',
      credits: 100,          // Halved from 200
      ideasEquivalent: 8,    // ~8 ideas at 12 credits each
      description: '~8 ideas/month',
      isPopular: false,
    },
    {
      id: 'growth_monthly',
      name: 'Growth',
      price: 9999,
      priceDisplay: '₹9,999/mo',
      credits: 200,          // Halved from 400
      ideasEquivalent: 16,   // ~16 ideas at 12 credits each
      description: '~16 ideas/month',
      isPopular: true,
    },
    {
      id: 'scale_monthly',
      name: 'Scale',
      price: 17999,
      priceDisplay: '₹17,999/mo',
      credits: 400,          // Halved from 800
      ideasEquivalent: 33,   // ~33 ideas at 12 credits each
      description: '~33 ideas/month',
      isPopular: false,
    },
  ],
  
  // Add-on credit packs (small top-ups with 90-day expiry)
  // NOTE: Large one-time packs have been REMOVED to avoid liability
  // Add-ons are premium-priced for convenience (₹35-40/credit vs ₹25 subscription)
  CREDIT_PACKS: [
    {
      id: 'addon_50',
      name: '+50 Credits',
      price: 1999,
      priceDisplay: '₹1,999',
      credits: 50,
      ideasEquivalent: 4,    // ~4 ideas at 12 credits each
      description: '~4 ideas • Expires 90 days from purchase',
      isAddon: true,
    },
    {
      id: 'addon_100',
      name: '+100 Credits',
      price: 3499,
      priceDisplay: '₹3,499',
      credits: 100,
      ideasEquivalent: 8,    // ~8 ideas at 12 credits each
      description: '~8 ideas • Expires 90 days from purchase',
      isAddon: true,
    },
  ],
  
  // Brand limits
  MAX_BRANDS_PER_USER: 1,
  
  // Refinement caps
  FREE_REFINEMENTS_PER_CONTENT: 3,
};

// Helper functions
export const creditsToPostsDisplay = (credits) => {
  return Math.floor(credits / PRICING_CONFIG.CREDIT_COSTS.FULL_POST);
};

export const getSubscriptionById = (id) => {
  return PRICING_CONFIG.SUBSCRIPTIONS.find(s => s.id === id);
};

export const getCreditPackById = (id) => {
  return PRICING_CONFIG.CREDIT_PACKS.find(p => p.id === id);
};

export default PRICING_CONFIG;
